<template>
  <div class="cashback-regra-combinacoes">
    <div class="cashback-regra-combinacoes__section">
      <div class="cashback-regra__secao-titulo">Combinações com outros benefícios</div>
      <div class="cashback-regra-combinacoes__toggles">
        <BooleanToggle
          v-for="combinacao in combinacoes"
          :key="combinacao.campo"
          :label="combinacao.titulo"
          :help="combinacao.ajuda"
          :value="!!item[combinacao.campo]"
          @input="alterar(combinacao.campo, $event)"
        />
      </div>
      <div v-if="item.permiteComUsoCashback" class="cashback-regra-ganho-uso" role="radiogroup" aria-labelledby="cashback-regra-ganho-uso-titulo">
        <div id="cashback-regra-ganho-uso-titulo" class="cashback-regra__rotulo">Ganho quando o pedido usa saldo de cashback</div>
        <label
          v-for="opcao in ganhosComUso"
          :key="opcao.chave"
          class="cashback-regra-ganho-uso__opcao"
          :class="{ 'cashback-regra-ganho-uso__opcao--ativa': (item.baseComUso || 'TOTAL') === opcao.chave }"
        >
          <input type="radio" name="cashback-regra-ganho-uso" :value="opcao.chave" :checked="(item.baseComUso || 'TOTAL') === opcao.chave" @change="alterar('baseComUso', opcao.chave)" />
          <span>
            <strong>{{ opcao.titulo }}</strong>
            <small>{{ opcao.descricao }}</small>
          </span>
        </label>
      </div>
      <small class="cashback-regra-combinacoes__dica">Pedidos bonificados nunca geram cashback.</small>
    </div>

    <div class="cashback-regra-combinacoes__section">
      <div class="cashback-regra__secao-titulo">Elegibilidade do cliente</div>
      <CRow>
        <CCol md="6">
          <InterruptorCashback
            rotulo="Títulos vencidos"
            texto-ligado="Cliente com título vencido não ganha cashback"
            texto-desligado="Não verifica títulos vencidos"
            ajuda="Cliente com título vencido além da tolerância não ganha cashback no pedido."
            :value="!!item.exigeSemTituloVencido"
            @input="alterar('exigeSemTituloVencido', $event)"
          />
        </CCol>
        <CCol md="3">
          <label class="cashback-regra__rotulo">Tolerância de atraso (dias)</label>
          <input
            class="form-control"
            type="number"
            min="0"
            step="1"
            :value="item.toleranciaDiasTituloVencido"
            :disabled="!item.exigeSemTituloVencido"
            @input="alterar('toleranciaDiasTituloVencido', numeroDigitado($event))"
          />
        </CCol>
        <CCol md="3">
          <label class="cashback-regra__rotulo">Contagem dos dias</label>
          <select
            class="form-control"
            :value="item.contagemDiasTituloVencido"
            :disabled="!item.exigeSemTituloVencido"
            @change="alterar('contagemDiasTituloVencido', $event.target.value)"
          >
            <option v-for="contagem in contagensDias" :key="contagem.valor" :value="contagem.valor">{{ contagem.rotulo }}</option>
          </select>
          <small class="cashback-regra-combinacoes__dica">{{ dicaContagemDias }}</small>
        </CCol>
      </CRow>
      <CRow class="mt-3">
        <CCol md="4">
          <label class="cashback-regra__rotulo">Média de atraso máxima (dias)</label>
          <input
            class="form-control"
            type="number"
            min="0"
            step="1"
            placeholder="Não verifica"
            :value="item.mediaAtrasoMaximaDias"
            @input="alterar('mediaAtrasoMaximaDias', numeroDigitado($event))"
          />
        </CCol>
        <CCol md="4">
          <label class="cashback-regra__rotulo">Considerar os últimos (meses)</label>
          <input
            class="form-control"
            type="number"
            min="1"
            max="36"
            step="1"
            placeholder="12"
            :value="item.mediaAtrasoMeses"
            :disabled="item.mediaAtrasoMaximaDias === null || item.mediaAtrasoMaximaDias === undefined"
            @input="alterar('mediaAtrasoMeses', numeroDigitado($event))"
          />
        </CCol>
        <CCol md="4" class="d-flex align-items-end">
          <small class="cashback-regra-combinacoes__dica mb-2">
            Média de dias entre o vencimento e o pagamento dos títulos pagos no período (pagamento antecipado conta 0). Cliente acima da média
            não ganha; cliente sem título pago no período ganha normalmente. Vazio: não verifica.
          </small>
        </CCol>
      </CRow>
    </div>

    <div class="cashback-regra-combinacoes__section">
      <div class="cashback-regra__secao-titulo">Validade</div>
      <CRow>
        <CCol md="4">
          <label class="cashback-regra__rotulo">Validade do cashback (dias)</label>
          <input class="form-control" type="number" min="0" step="1" placeholder="Sem prazo" :value="item.validadeDias" @input="alterar('validadeDias', numeroDigitado($event))" />
        </CCol>
        <CCol md="8" class="d-flex align-items-end">
          <small class="cashback-regra-combinacoes__dica mb-2">
            Contados a partir do faturamento. Após o prazo, o saldo não usado expira. Vazio ou 0: o cashback não tem prazo de validade.
          </small>
        </CCol>
      </CRow>
    </div>

    <div class="cashback-regra-combinacoes__section">
      <div class="cashback-regra__secao-titulo">Limites</div>
      <CRow>
        <CCol md="3">
          <label class="cashback-regra__rotulo">Por pedido</label>
          <InputMoney :value="item.limiteValorPedido" @input="alterar('limiteValorPedido', $event)" />
        </CCol>
        <CCol md="3">
          <label class="cashback-regra__rotulo">Por cliente</label>
          <InputMoney :value="item.limiteValorCliente" @input="alterar('limiteValorCliente', $event)" />
        </CCol>
        <CCol md="3">
          <label class="cashback-regra__rotulo">Total da regra</label>
          <InputMoney :value="item.limiteValorTotal" @input="alterar('limiteValorTotal', $event)" />
        </CCol>
        <CCol md="3">
          <label class="cashback-regra__rotulo">Período</label>
          <select class="form-control" :value="item.limitePeriodo || ''" @change="alterar('limitePeriodo', $event.target.value || null)">
            <option value="">Acumulado</option>
            <option v-for="periodo in opcoes.periodos" :key="periodo.chave" :value="periodo.chave">
              {{ periodo.label }}
            </option>
          </select>
        </CCol>
      </CRow>
      <small class="cashback-regra-combinacoes__dica">
        Deixe zerado para não limitar. O período vale para os limites por cliente e total da regra.
      </small>
    </div>
  </div>
</template>

<script>
import BooleanToggle from "@/views/Campanhas/components/CampanhasComerciais/shared/BooleanToggle.vue";
import InputMoney from "@/components/InputMoney";
import InterruptorCashback from "../components/InterruptorCashback.vue";
import { COMBINACOES, CONTAGENS_DIAS_TITULO, GANHOS_COM_USO } from "../helpers";

export default {
  name: "StepCombinacoesLimites",
  components: { BooleanToggle, InputMoney, InterruptorCashback },

  props: {
    item: { type: Object, required: true },
    opcoes: { type: Object, required: true },
  },

  data() {
    return {
      combinacoes: COMBINACOES,
      ganhosComUso: GANHOS_COM_USO,
      contagensDias: CONTAGENS_DIAS_TITULO,
    };
  },

  computed: {
    dicaContagemDias() {
      return (this.contagensDias.find((contagem) => contagem.valor === this.item.contagemDiasTituloVencido) || this.contagensDias[0]).dica;
    },
  },

  methods: {
    alterar(campo, valor) {
      this.$emit("alterar", { [campo]: valor });
    },

    numeroDigitado(evento) {
      const valor = evento.target.value;

      return valor === "" ? null : Number(valor);
    },
  },
};
</script>

<style scoped>
.cashback-regra-ganho-uso {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.cashback-regra-ganho-uso__opcao {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  cursor: pointer;
}

.cashback-regra-ganho-uso__opcao input {
  margin-top: 3px;
}

.cashback-regra-ganho-uso__opcao span {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cashback-regra-ganho-uso__opcao small {
  color: #667085;
}

.cashback-regra-ganho-uso__opcao--ativa {
  border-color: var(--cashback-cor-primaria, #3a3e81);
  background-color: var(--cashback-cor-primaria-suave, #eceaf3);
}

.cashback-regra-combinacoes__section {
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.cashback-regra-combinacoes__section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.cashback-regra__secao-titulo {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.cashback-regra__rotulo {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.cashback-regra-combinacoes__toggles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
}

.cashback-regra-combinacoes__dica {
  display: block;
  font-size: 11px;
  color: #999;
  margin-top: 6px;
}

@media (max-width: 768px) {
  .cashback-regra-combinacoes__toggles {
    grid-template-columns: 1fr;
  }
}
</style>
